// register.js — adapter "modulo amm" (design §10, upgrade R4): Amministrazione
// NON usa Workspaces (admin-module gestisce body.workspace-amm via #amm-rail e
// #amm-sidebar) e admin-module.js NON si modifica. L'adapter registra 'amm'
// nella shell (host id: l'attivazione resta il click programmatico sul rail
// legacy) e porta la nav interna nel sottomenù unificato via BRIDGE DICHIARATIVO
// (§6.2): ogni voce = bridgeEntry su `#amm-sidebar .amm-link[data-page="…"]`
// (selettori ad attributo: autoportanti) + testo atteso come guardia anti-deriva.
// La parità voce-per-voce con NAV_GROUPS di admin-module.js è asserita da
// server/src/lib/shell-guest-adapters.test.js.
//
// assertBridge a ogni ingresso nel modulo: un selettore rotto ⇒ fallback
// AUTOMATICO submenu:'internal' + toast (MAI proxy silenzioso). A bridge verde
// l'adapter riallinea la voce attiva del sottomenù su AMM.currentPage.
//
// CSS SCOPED: #amm-sidebar (e l'hamburger mobile che la apre) si nascondono SOLO
// con body.shell-submenu + body.workspace-amm; fallback/flag ⇒ ricompaiono da soli.
//
// GUARDIA KILL-SWITCH identica a proc-v0 + flag per-modulo shell_submenu_amm='0'.

import { proxyAmm } from '../shell/legacy-bridge.js';
import { moduleSubmenuEnabled } from '../shell/flags.js';
import { label, moduleLabel } from '../shell/glossary.js';
import {
  bridgeEntry, bridgeNav, flatEntries, clickBridgeRetry,
  installBridgeGuard, injectStyleOnce,
} from '../shell/bridge.js';

function ammEntry(page, glossKey) {
  return bridgeEntry(page, glossKey, {
    selector: '#amm-sidebar .amm-link[data-page="' + page + '"]',
    text: label(glossKey),
  });
}

// F3.3 (ondata 16): dall'Action Hub al RECORD scadenza. Deep-link con params
// {from:'dash-actions', kind:'scadenza', id} ⇒ si apre l'editor della scadenza
// con la STESSA via della ricerca globale amm (admin-module.js:1373):
// api.amm.scadenze.get(id) → AMM.openEditor(rec). L'editor è un overlay:
// funziona da qualunque pagina; sotto resta il Cruscotto amm (il click-bridge
// della vista gira in parallelo). admin-module.js NON si tocca (off-limits R4):
// si usa solo la sua API pubblica. Ogni fallimento ⇒ toast, MAI silenzio.
function maybeOpenScadenza(win, params) {
  const p = params || {};
  if (p.from !== 'dash-actions' || p.kind !== 'scadenza' || !p.id) return;
  const bus = win.Workspaces && win.Workspaces.bus;
  function fail(msg) {
    console.error('[amm] deep-link scadenza "' + p.id + '": ' + msg);
    if (bus) {
      try { bus.emit('toast', { kind: 'error', msg: 'Scadenza non aperta: ' + msg }); } catch (e) { console.error(e); }
    }
  }
  const get = win.api && win.api.amm && win.api.amm.scadenze && win.api.amm.scadenze.get;
  const canOpen = win.AMM && typeof win.AMM.openEditor === 'function';
  if (typeof get !== 'function' || !canOpen) { fail('modulo Amministrazione non pronto'); return; }
  get(p.id).then(
    (rec) => { win.AMM.openEditor(rec); },
    (e) => { fail((e && e.message) || 'errore di caricamento'); }
  );
}

export const AMM_SECTIONS = [
  { section: 'amm.section.main', items: [
    ammEntry('cruscotto', 'amm.cruscotto'),
    ammEntry('resoconto', 'amm.resoconto'),
    ammEntry('personale', 'amm.personale'),
    ammEntry('voci', 'amm.voci'),
    ammEntry('esercizi', 'amm.esercizi'),
    ammEntry('calendario', 'amm.calendario'),
    ammEntry('scadenzario', 'amm.scadenzario'),
    ammEntry('f24', 'amm.f24'),
    ammEntry('tributi', 'amm.tributi'),
    ammEntry('atti', 'amm.atti'),
    ammEntry('rateizzazioni', 'amm.rateizzazioni'),
    ammEntry('riepilogo', 'amm.riepilogo'),
    ammEntry('profilo', 'amm.profilo'),
  ] },
  { section: 'amm.section.anagrafiche', items: [
    ammEntry('veicoli', 'amm.veicoli'),
    ammEntry('polizze', 'amm.polizze'),
    ammEntry('documenti', 'amm.documenti'),
    ammEntry('dipendenti', 'amm.dipendenti'),
    ammEntry('immobili', 'amm.immobili'),
    ammEntry('crediti', 'amm.crediti'),
    ammEntry('cantieri', 'amm.cantieri'),
  ] },
];

export const AMM_HIDE_CSS = [
  'body.shell-submenu.workspace-amm #amm-sidebar { display:none !important; }',
  // mobile: l'hamburger amm apre il drawer di #amm-sidebar (che qui è nascosta):
  // si spegne anche lui — le sezioni passano dallo sheet della MobileBar.
  '@media (max-width:767px){ body.shell-submenu.workspace-amm #amm-hamburger { display:none !important; } }',
].join('\n');

export function installAmmV0(win) {
  const W = win.Workspaces;
  // guardia kill-switch identica a proc-v0: mai registrarsi su workspace-host
  if (!W || W.version !== 2 || typeof W.navigate !== 'function' || !W.bus) return false;
  const entries = flatEntries(AMM_SECTIONS);
  const hasBridgeApi = typeof W.update === 'function';
  const useShell = hasBridgeApi && moduleSubmenuEnabled(win, 'amm');
  W.register({
    id: 'amm',
    version: 2,
    submenu: useShell ? 'shell' : 'internal',
    label: 'Amministrazione', // nella shell vince comunque il glossario (B2.17)
    nav: bridgeNav(AMM_SECTIONS),
    onShow: () => { proxyAmm(win.document); },
    navigate: (itemId, params) => {
      clickBridgeRetry(win, entries, itemId);
      maybeOpenScadenza(win, params); // F3.3: overlay record sopra la pagina
    },
    openDeepLink: (l) => {
      if (!l || typeof l.item !== 'string') return false;
      if (!entries.some((e) => e.id === l.item)) return false;
      return clickBridgeRetry(win, entries, l.item);
    },
  });
  if (useShell) {
    injectStyleOnce(win.document, 'shell-subnav-amm-style', AMM_HIDE_CSS);
    installBridgeGuard(win, {
      moduleId: 'amm',
      entries,
      ready: (doc) => doc.querySelectorAll('#amm-sidebar .amm-link').length > 0,
      moduleLabelText: moduleLabel('amm'),
      // restore di boot: admin-module si auto-ripristina SENZA passare dalla
      // shell (nessun module-changed) — il guard controlla la classe body.
      activeNow: () => !!(win.document.body && win.document.body.classList.contains('workspace-amm')),
      // bridge verde a ogni ingresso: la voce attiva del sottomenù = pagina
      // corrente di admin-module (restore compreso), mai colonna senza attivo.
      onActive: () => {
        const cur = win.AMM && win.AMM.currentPage;
        try {
          W.bus.emit('shell:navigate', { module: 'amm', item: (typeof cur === 'string' && cur) || 'cruscotto' });
        } catch (e) { console.error(e); }
      },
    });
  }
  return true;
}

// side-effect SOLO nel browser (i contract test importano il file sotto node)
if (typeof window !== 'undefined') installAmmV0(window);
